import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LogOut, Users, Crown, ChevronDown, Copy, HelpCircle, X, AlertTriangle } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

import WatchPartyPlayer from "../components/WatchPartyPlayer";
import YouTubePlayer from "../components/YouTubePlayer";
import VimeoPlayer from "../components/VimeoPlayer";
import DailymotionPlayer from "../components/DailymotionPlayer";
import MetadataSyncPlayer from "../components/MetadataSyncPlayer";
import WatchPartyChat from "../components/WatchPartyChat";
import watchPartySocketManager from "../services/watchPartySocket";

function WatchPartyRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [party, setParty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [isHost, setIsHost] = useState(false);

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const [chatPosition, setChatPosition] = useState({ x: 20, y: 20 });
  const [chatSize, setChatSize] = useState({ width: 380, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const videoContainerRef = useRef(null);
  const chatOverlayRef = useRef(null);
  /** After `joined-room`, isHost comes from the socket; avoid overwriting from API refresh. */
  const hostIdentityFromSocketRef = useRef(false);

  const [copied, setCopied] = useState(null); // "party" | "user" | "username" | null
  const [showHowTo, setShowHowTo] = useState(false);
  const [showNsfwWarning, setShowNsfwWarning] = useState(false);
  const [nsfwAccepted, setNsfwAccepted] = useState(false);

  const handleJoinedRoom = useCallback((data) => {
    if (data && typeof data.isHost === "boolean") {
      hostIdentityFromSocketRef.current = true;
      setIsHost((prev) => (prev === data.isHost ? prev : data.isHost));
    }
  }, []);

  const copyToClipboard = useCallback(async (text, key) => {
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(key);
      window.setTimeout(() => setCopied(null), 900);
    } catch {
      // fallback
      try {
        const ta = document.createElement("textarea");
        ta.value = String(text);
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(key);
        window.setTimeout(() => setCopied(null), 900);
      } catch {
        // no-op
      }
    }
  }, []);

  const fetchPartyDetails = useCallback(async () => {
    try {
      const response = await axios.get(`/api/watch-parties/${id}`);
      setParty(response.data);
      if (
        user?.id != null &&
        !hostIdentityFromSocketRef.current
      ) {
        setIsHost(
          Number(response.data.host_user_id) === Number(user.id)
        );
      }
    } catch (err) {
      console.error("Error refreshing party:", err);
    }
  }, [id, user?.id]);

  /**
   * STOP AUTO-SCROLL ON ENTRY (robust)
   * This beats:
   * - browser scroll restoration
   * - child focus() / scrollIntoView() firing slightly later
   */
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useLayoutEffect(() => {
    // Run before paint
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Run after paint + again after a short delay (beats late focus in children)
    const t1 = window.setTimeout(() => window.scrollTo(0, 0), 0);
    const t2 = window.setTimeout(() => window.scrollTo(0, 0), 150);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  // Check authentication
  useEffect(() => {
    if (!user) {
      alert("Please log in to join watch parties");
      navigate("/login");
    }
  }, [user, navigate]);

  // Close status dropdown on outside click / Esc
  useEffect(() => {
    if (!showStatusDropdown) return;

    const onDown = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setShowStatusDropdown(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setShowStatusDropdown(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showStatusDropdown]);

  // Fetch party details
  useEffect(() => {
    if (!user) return;

    const fetchParty = async () => {
      try {
        const response = await axios.get(`/api/watch-parties/${id}`);
        setParty(response.data);
        setIsHost(
          Number(response.data.host_user_id) === Number(user.id)
        );
      } catch (err) {
        console.error("Error fetching party:", err);
        setError("Failed to load watch party");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchParty();
  }, [id, user]);

  useEffect(() => {
    hostIdentityFromSocketRef.current = false;
  }, [id]);

  // Check for NSFW chat warning
  useEffect(() => {
    if (!party || !user || isHost) return;

    // Show NSFW warning if chat is unfiltered and user hasn't accepted yet
    if (party.allow_nsfw_chat && !nsfwAccepted) {
      setShowNsfwWarning(true);
    }
  }, [party, user, isHost, nsfwAccepted]);

  // Join party and connect socket
  useEffect(() => {
    if (!party || !user || hasJoined) return;

    // If NSFW chat is enabled and user hasn't accepted, don't join yet
    if (party.allow_nsfw_chat && !nsfwAccepted && !isHost) {
      return;
    }

    const joinParty = async () => {
      setIsJoining(true);

      try {
        if (!isHost) {
          await axios.post(
            `/api/watch-parties/${id}/join`,
            {},
            { withCredentials: true }
          );
        }

        watchPartySocketManager.connect(
          id,
          user.id,
          user.username,
          user.profile_picture
        );

        if (isHost && party.status === "waiting") {
          try {
            await axios.patch(
              `/api/watch-parties/${id}`,
              { status: "live" },
              { withCredentials: true }
            );
            await fetchPartyDetails();
          } catch (err) {
            console.error("Error starting party:", err);
          }
        }

        setHasJoined(true);
      } catch (err) {
        console.error("Error joining party:", err);
        if (err.response?.status === 403) {
          alert("This party is full");
          navigate("/watch-parties");
        } else if (
          err.response?.status === 400 &&
          err.response?.data?.error === "You are already in this party"
        ) {
          setHasJoined(true);
        } else {
          alert("Failed to join party");
          navigate("/watch-parties");
        }
      } finally {
        setIsJoining(false);
      }
    };

    joinParty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party?.id, user?.id]);

  // Cleanup socket on component unmount
  useEffect(() => {
    return () => {
      watchPartySocketManager.disconnect();
    };
  }, []);

  // Stable socket listeners after room join
  useEffect(() => {
    if (!hasJoined) return;

    const handlePartyEnded = () => {
      alert("The host has ended this party");
      watchPartySocketManager.disconnect();
      navigate("/watch-parties");
    };
    const handleUserJoined = () => fetchPartyDetails();
    const handleUserLeft = () => fetchPartyDetails();

    watchPartySocketManager.on("party-ended", handlePartyEnded);
    watchPartySocketManager.on("user-joined", handleUserJoined);
    watchPartySocketManager.on("user-left", handleUserLeft);

    return () => {
      watchPartySocketManager.off("party-ended", handlePartyEnded);
      watchPartySocketManager.off("user-joined", handleUserJoined);
      watchPartySocketManager.off("user-left", handleUserLeft);
    };
  }, [hasJoined, fetchPartyDetails, navigate]);

  // Server-authoritative host flag from socket (overrides API once received)
  useEffect(() => {
    if (!hasJoined) return;

    watchPartySocketManager.setOnJoinedRoom(handleJoinedRoom);
    return () => {
      watchPartySocketManager.setOnJoinedRoom(null);
    };
  }, [hasJoined, handleJoinedRoom]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);

      if (isNowFullscreen) {
        setChatPosition({ x: 20, y: 20 });
        setChatSize({ width: 380, height: 600 });
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Helpers for mouse/touch coordinates
  const getPoint = (e) => {
    if (e?.touches?.[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    if (e?.changedTouches?.[0]) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  // Handle chat dragging (fullscreen overlay) — mouse + touch
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      // prevent page scroll while dragging on mobile
      if (e.cancelable) e.preventDefault();

      const pt = getPoint(e);
      const deltaX = pt.x - dragStart.x;
      const deltaY = pt.y - dragStart.y;

      setChatPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x + deltaX, window.innerWidth - chatSize.width)),
        y: Math.max(0, Math.min(prev.y + deltaY, window.innerHeight - 100)),
      }));

      setDragStart({ x: pt.x, y: pt.y });
    };

    const handleUp = () => setIsDragging(false);

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);

    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleUp);

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);

      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, dragStart, chatSize.width]);

  // Handle chat resizing (fullscreen overlay) — mouse + touch
  useEffect(() => {
    if (!isResizing) return;

    const handleMove = (e) => {
      if (e.cancelable) e.preventDefault();

      const pt = getPoint(e);
      const deltaX = pt.x - dragStart.x;
      const deltaY = pt.y - dragStart.y;

      setChatSize((prev) => ({
        width: Math.max(320, Math.min(prev.width + deltaX, 640)),
        height: Math.max(
          380,
          Math.min(prev.height + deltaY, window.innerHeight - chatPosition.y - 20)
        ),
      }));

      setDragStart({ x: pt.x, y: pt.y });
    };

    const handleUp = () => setIsResizing(false);

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);

    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleUp);

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);

      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleUp);
    };
  }, [isResizing, dragStart, chatPosition.y]);

  const handleDragStart = (e) => {
    e.stopPropagation();
    const pt = getPoint(e);
    setIsDragging(true);
    setDragStart({ x: pt.x, y: pt.y });
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    const pt = getPoint(e);
    setIsResizing(true);
    setDragStart({ x: pt.x, y: pt.y });
  };

  const handleLeave = async () => {
    try {
      await axios.post(
        `/api/watch-parties/${id}/leave`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      console.error("Error leaving party:", err);
    } finally {
      watchPartySocketManager.disconnect();
      navigate("/watch-parties");
    }
  };

  const handleStatusChange = async (newStatus) => {
    setShowStatusDropdown(false);
    if (!party || newStatus === party.status) return;

    try {
      const response = await axios.patch(
        `/api/watch-parties/${id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      console.log("Status updated:", response.data);

      if (newStatus === "ended") {
        watchPartySocketManager.endParty();
        alert("Party ended successfully");
        navigate("/watch-parties");
      } else {
        await fetchPartyDetails();
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status: " + (err.response?.data?.error || err.message));
    }
  };

  const handleEndParty = async () => {
    if (!window.confirm("Are you sure you want to end this party?")) return;
    await handleStatusChange("ended");
  };

  const statusMeta = useMemo(() => {
    const s = String(party?.status || "waiting").toLowerCase();
    const map = {
      waiting: {
        label: "Waiting",
        dot: "bg-yellow-400",
        pill: "bg-yellow-400/10 text-yellow-200 border-yellow-400/20",
      },
      live: {
        label: "Live",
        dot: "bg-emerald-400",
        pill: "bg-emerald-400/10 text-emerald-200 border-emerald-400/20",
      },
      ended: {
        label: "Ended",
        dot: "bg-rose-400",
        pill: "bg-rose-400/10 text-rose-200 border-rose-400/20",
      },
    };
    return map[s] || map.waiting;
  }, [party?.status]);

  const isYouTube = party?.platform === "YOUTUBE";
  const isVimeo = party?.platform === "VIMEO";
  const isDailymotion = party?.platform === "DAILYMOTION";
  const isExternalPlatform = [
    "CRUNCHYROLL",
    "NETFLIX",
    "HULU",
    "DISNEY_PLUS",
    "FUNIMATION",
  ].includes(party?.platform);

  const videoSource = useMemo(() => {
    if (!party) return null;
    if (isYouTube || isVimeo || isDailymotion) return party.video_url;
    if (isExternalPlatform) return party.video_url;
    return `${window.location.origin}${party.video_url}`;
  }, [party, isYouTube, isVimeo, isDailymotion, isExternalPlatform]);

  if (loading || isJoining) {
    return (
      <div className="min-h-screen bg-[#050712] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(16,185,129,0.05),transparent_60%),radial-gradient(circle_at_80%_20%,rgba(139,92,246,0.045),transparent_65%)]" />
        <div className="relative min-h-screen flex items-center justify-center px-6">
          <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_30px_80px_rgba(0,0,0,0.55)] p-6 text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-2xl border border-white/10 bg-white/[0.05] flex items-center justify-center">
              <div className="h-6 w-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="text-sm text-white/70">
              {isJoining ? "Joining party..." : "Loading..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !party) {
    return (
      <div className="min-h-screen bg-[#050712] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_15%,rgba(139,92,246,0.05),transparent_60%),radial-gradient(circle_at_80%_70%,rgba(16,185,129,0.045),transparent_65%)]" />
        <div className="relative min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_30px_80px_rgba(0,0,0,0.55)] p-6">
            <p className="text-rose-300 font-medium mb-4">
              {error || "Party not found"}
            </p>
            <button
              onClick={() => navigate("/watch-parties")}
              className="w-full rounded-2xl border border-white/10 bg-white/[0.06] hover:bg-white/[0.10] transition px-4 py-3 text-sm font-semibold text-white/90"
            >
              Back to Browse
            </button>
          </div>
        </div>
      </div>
    );
  }

  const participants = party.participants || [];

  return (
    <div
      className="
        min-h-screen bg-[#050712] text-white
        pb-[calc(env(safe-area-inset-bottom)+12px)]
      "
    >
      {/* Background (subtle + clean) */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(16,185,129,0.045),transparent_55%),radial-gradient(circle_at_80%_20%,rgba(139,92,246,0.04),transparent_60%),radial-gradient(circle_at_50%_90%,rgba(255,255,255,0.025),transparent_65%)]" />
        <div className="absolute inset-0 opacity-[0.18] bg-[linear-gradient(to_right,rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:56px_56px]" />
      </div>

      {/* Mobile-first: tighter padding on xs, unchanged on sm+ */}
      <div className="relative mx-auto w-full max-w-[1860px] px-3 sm:px-6 py-4 sm:py-7">
        {/* Header Card */}
        <div className="relative z-40 rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_30px_80px_rgba(0,0,0,0.55)]">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-white/95 truncate">
                    {party.title}
                  </h1>

                  <span
                    className={`hidden sm:inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${statusMeta.pill}`}
                    title="Party status"
                  >
                    <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
                    {statusMeta.label}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/60">
                  <div className="inline-flex items-center gap-2">
                    <Crown className="h-4 w-4 text-violet-300/90" />
                    <span className="truncate">
                      Hosted by{" "}
                      <span className="text-white/80">{party.host_username}</span>
                    </span>
                  </div>

                  <div className="inline-flex items-center gap-2">
                    <Users className="h-4 w-4 text-emerald-300/90" />
                    <span>
                      <span className="text-white/80">{participants.length}</span>{" "}
                      / {party.max_capacity}
                    </span>
                  </div>

                  <span className="sm:hidden inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-semibold text-white/70">
                    <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
                    {statusMeta.label}
                  </span>
                </div>

                {/* Party ID */}
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="inline-flex w-fit items-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-3 py-2">
                    <span className="text-[11px] font-semibold tracking-[0.26em] uppercase text-white/50">
                      Party ID
                    </span>
                    <code className="text-xs font-mono text-emerald-300/90">
                      {id}
                    </code>
                  </div>

                  <button
                    onClick={() => copyToClipboard(id, "party")}
                    className="
                      inline-flex items-center justify-center gap-2
                      rounded-2xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.09] transition
                      px-3 py-2 text-sm font-semibold text-white/85
                      w-full sm:w-auto
                    "
                  >
                    <Copy className="h-4 w-4" />
                    {copied === "party" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              {/* Mobile-first: full-width buttons on xs; desktop unchanged */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-end mt-1 lg:mt-3">
                {isHost && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <div className="relative z-[70] w-full sm:w-auto" ref={dropdownRef}>
                      <button
                        onClick={() => setShowStatusDropdown((v) => !v)}
                        className="
                          inline-flex items-center justify-center gap-2
                          rounded-2xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.09] transition
                          px-4 py-2.5 text-sm font-semibold text-white/85
                          w-full sm:w-auto
                        "
                      >
                        <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} />
                        <span className="capitalize">{party.status}</span>
                        <ChevronDown className="h-4 w-4 text-white/60" />
                      </button>

                      {showStatusDropdown && (
                        <div className="absolute right-0 mt-2 w-full sm:w-44 overflow-hidden rounded-2xl border border-white/10 bg-[#0a0f22]/95 backdrop-blur-md shadow-[0_20px_60px_rgba(0,0,0,0.55)] z-[80]">
                          <button
                            onClick={() => handleStatusChange("waiting")}
                            className="w-full px-4 py-3 text-left text-sm text-white/80 hover:bg-white/[0.06] transition flex items-center gap-2"
                          >
                            <span className="h-2 w-2 rounded-full bg-yellow-400" />
                            Waiting
                          </button>
                          <button
                            onClick={() => handleStatusChange("live")}
                            className="w-full px-4 py-3 text-left text-sm text-white/80 hover:bg-white/[0.06] transition flex items-center gap-2"
                          >
                            <span className="h-2 w-2 rounded-full bg-emerald-400" />
                            Live
                          </button>
                          <button
                            onClick={() => handleStatusChange("ended")}
                            className="w-full px-4 py-3 text-left text-sm text-rose-300 hover:bg-rose-500/10 transition flex items-center gap-2 border-t border-white/10"
                          >
                            <span className="h-2 w-2 rounded-full bg-rose-400" />
                            End Party
                          </button>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleEndParty}
                      className="
                        inline-flex items-center justify-center
                        rounded-2xl border border-rose-500/25 bg-rose-500/10 hover:bg-rose-500/15 transition
                        px-4 py-2.5 text-sm font-semibold text-rose-200
                        w-full sm:w-auto
                      "
                    >
                      End
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setShowHowTo(true)}
                  className="
                    inline-flex items-center justify-center gap-2
                    rounded-2xl border border-emerald-400/25 bg-emerald-400/10 hover:bg-emerald-400/15 transition
                    px-4 py-2.5 text-sm font-semibold text-emerald-200
                    w-full sm:w-auto
                  "
                >
                  <HelpCircle className="h-4 w-4" />
                  How To
                </button>

                <button
                  onClick={handleLeave}
                  className="
                    inline-flex items-center justify-center gap-2
                    rounded-2xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.09] transition
                    px-4 py-2.5 text-sm font-semibold text-white/85
                    w-full sm:w-auto
                  "
                >
                  <LogOut className="h-4 w-4" />
                  Leave
                </button>
              </div>
            </div>
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {isExternalPlatform && (
            <div className="p-4 sm:p-6">
              <div className="rounded-3xl border border-violet-400/20 bg-gradient-to-br from-violet-500/05 via-white/[0.035] to-emerald-500/05 backdrop-blur-md p-5 sm:p-6">
                {/* ... unchanged banner content ... */}
              </div>
            </div>
          )}
        </div>

        {/* Main Layout */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[260px_1fr_420px] gap-4">
          {/* Participants (Desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-6 rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_24px_70px_rgba(0,0,0,0.45)] overflow-hidden">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-white/80 inline-flex items-center gap-2">
                    <Users className="h-4 w-4 text-emerald-300/90" />
                    Participants
                  </h3>
                  <div className="text-xs text-white/50">
                    {participants.length} / {party.max_capacity}
                  </div>
                </div>
              </div>

              <div className="h-px w-full bg-white/10" />

              <div className="p-3 max-h-[calc(100vh-260px)] overflow-auto">
                <div className="space-y-2">
                  {participants.map((p) => (
                    <div
                      key={p.user_id}
                      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-3 py-2"
                    >
                      <img
                        src={p.profile_picture || "https://i.pravatar.cc/32"}
                        alt={p.username}
                        className="h-8 w-8 rounded-full border border-white/10 object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white/85 truncate">
                          {p.username}
                          {p.user_id === party.host_user_id && (
                            <span className="ml-2 inline-flex items-center rounded-full border border-violet-400/20 bg-violet-500/10 px-2 py-0.5 text-[11px] font-semibold text-violet-200">
                              Host
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/45 font-mono truncate">
                          #{p.user_id}
                        </div>
                      </div>
                    </div>
                  ))}
                  {participants.length === 0 && (
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm text-white/60">
                      No participants yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>

          {/* Video */}
          <section className="min-w-0">
            <div
              ref={videoContainerRef}
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-black shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_28px_90px_rgba(0,0,0,0.55)]"
            >
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/[0.06]" />

              <div className="relative w-full">
                {isExternalPlatform ? (
                  <MetadataSyncPlayer
                    videoUrl={videoSource}
                    platform={party.platform}
                    isHost={isHost}
                    socketManager={watchPartySocketManager}
                    partyStatus={party.status}
                    episodeInfo={{
                      season_number: party.season_number,
                      episode_number: party.episode_number,
                      episode_title: party.episode_title,
                    }}
                  />
                ) : isYouTube ? (
                  <YouTubePlayer
                    videoId={videoSource}
                    isHost={isHost}
                    socketManager={watchPartySocketManager}
                    partyStatus={party.status}
                    parentContainerRef={videoContainerRef}
                  />
                ) : isVimeo ? (
                  <VimeoPlayer
                    videoId={videoSource}
                    isHost={isHost}
                    socketManager={watchPartySocketManager}
                    partyStatus={party.status}
                  />
                ) : isDailymotion ? (
                  <DailymotionPlayer
                    videoId={videoSource}
                    isHost={isHost}
                    socketManager={watchPartySocketManager}
                    partyStatus={party.status}
                  />
                ) : (
                  <WatchPartyPlayer
                    videoUrl={videoSource}
                    isHost={isHost}
                    socketManager={watchPartySocketManager}
                    partyStatus={party.status}
                    parentContainerRef={videoContainerRef}
                  />
                )}

                {/* Fullscreen chat overlay */}
                {isFullscreen && (
                  <div
                    ref={chatOverlayRef}
                    className="absolute z-50 pointer-events-none"
                    style={{
                      left: `${chatPosition.x}px`,
                      top: `${chatPosition.y}px`,
                      width: `${chatSize.width}px`,
                      height: `${chatSize.height}px`,
                    }}
                  >
                    <div className="h-full pointer-events-auto rounded-3xl border border-white/10 bg-[#0a0f22]/70 backdrop-blur-md shadow-[0_20px_70px_rgba(0,0,0,0.60)] overflow-hidden relative">
                      <div
                        className="absolute top-0 left-0 right-0 h-10 cursor-move z-10 flex items-center justify-center bg-white/[0.03] hover:bg-white/[0.06]"
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                        style={{ pointerEvents: "auto" }}
                      >
                        <div className="h-1 w-12 rounded-full bg-white/25" />
                      </div>

                      <div
                        className="absolute bottom-0 right-0 w-10 h-10 cursor-nwse-resize z-10 flex items-center justify-center bg-white/[0.03] hover:bg-white/[0.06]"
                        onMouseDown={handleResizeStart}
                        onTouchStart={handleResizeStart}
                        style={{ pointerEvents: "auto" }}
                      >
                        <div className="w-3 h-3 border-r-2 border-b-2 border-white/40" />
                      </div>

                      <div className="h-full p-4 pt-10">
                        <WatchPartyChat
                          partyId={id}
                          socketManager={watchPartySocketManager}
                          participants={participants}
                          isFullscreen={true}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Participants (Mobile) */}
            <div className="lg:hidden mt-4 rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_24px_70px_rgba(0,0,0,0.45)] overflow-hidden">
              <div className="p-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white/80 inline-flex items-center gap-2">
                  <Users className="h-4 w-4 text-emerald-300/90" />
                  Participants
                </h3>
                <div className="text-xs text-white/50">{participants.length}</div>
              </div>
              <div className="h-px w-full bg-white/10" />
              <div className="p-4 flex flex-wrap gap-2">
                {participants.map((p) => (
                  <div
                    key={p.user_id}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-2"
                  >
                    <img
                      src={p.profile_picture || "https://i.pravatar.cc/32"}
                      alt={p.username}
                      className="h-6 w-6 rounded-full border border-white/10 object-cover"
                    />
                    <span className="text-sm text-white/80">
                      {p.username}
                      {p.user_id === party.host_user_id && (
                        <span className="ml-2 text-violet-300/90">★</span>
                      )}
                    </span>
                  </div>
                ))}
                {participants.length === 0 && (
                  <div className="text-sm text-white/60">No participants yet.</div>
                )}
              </div>
            </div>
          </section>

          {/* Chat (mobile gets a container + height; desktop remains unchanged via lg:contents) */}
          <aside className={isFullscreen ? "hidden" : "mt-4 lg:mt-0"}>
              {/* Mobile-first: give chat a stable viewport height */}
              <div className="h-[70vh] sm:h-[72vh] lg:h-auto">
                <WatchPartyChat
                  partyId={id}
                  socketManager={watchPartySocketManager}
                  participants={participants}
                />
              </div>
          </aside>
        </div>
      </div>

      {/* NSFW Warning Modal */}
      {showNsfwWarning && (
        <div
          className="fixed inset-0 z-[101] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="relative w-full max-w-md overflow-hidden rounded-[28px] border-2 border-orange-400/50 bg-[#070b1a]/98 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_24px_80px_rgba(0,0,0,0.75)] backdrop-blur-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="border-b border-orange-400/30 bg-gradient-to-br from-orange-400/20 to-red-400/20 p-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl border-2 border-orange-400/50 bg-orange-400/30 flex items-center justify-center">
                  <AlertTriangle className="h-7 w-7 text-orange-200" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-orange-100">
                    18+ Unfiltered Chat
                  </h3>
                  <p className="text-sm text-orange-200/80">
                    Content Warning
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-white/90">
                This watch party has <strong className="text-orange-300">unfiltered chat</strong> enabled by the host.
              </p>

              <div className="rounded-2xl border border-orange-400/30 bg-orange-400/10 p-4 space-y-2">
                <p className="text-sm text-orange-100/90 font-semibold">What this means:</p>
                <ul className="text-sm text-white/80 space-y-1 ml-4">
                  <li>• No profanity filtering</li>
                  <li>• Potentially mature content</li>
                  <li>• Recommended for ages 18+</li>
                </ul>
              </div>

              <p className="text-sm text-white/70">
                By accepting, you confirm you are 18 or older and comfortable with unfiltered chat content.
              </p>
            </div>

            {/* Actions */}
            <div className="border-t border-white/10 p-4 flex gap-3">
              <button
                onClick={() => {
                  setShowNsfwWarning(false);
                  navigate('/watch-parties');
                }}
                className="flex-1 rounded-2xl border border-white/20 bg-white/10 hover:bg-white/15 text-white/90 font-semibold py-3 transition"
              >
                Leave
              </button>
              <button
                onClick={() => {
                  setNsfwAccepted(true);
                  setShowNsfwWarning(false);
                }}
                className="flex-1 rounded-2xl border border-orange-400/30 bg-gradient-to-r from-orange-400/20 to-red-400/20 hover:from-orange-400/30 hover:to-red-400/30 text-orange-100 font-semibold py-3 transition"
              >
                I Accept (18+)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How To Modal */}
      {showHowTo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowHowTo(false)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-[28px] border border-white/10 bg-[#070b1a]/98 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_24px_80px_rgba(0,0,0,0.75)] backdrop-blur-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 border-b border-white/10 bg-emerald-400/10 p-5 backdrop-blur-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl border border-emerald-400/30 bg-emerald-400/20 flex items-center justify-center">
                    <HelpCircle className="h-5 w-5 text-emerald-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white/95">
                      How to Use Watch Parties
                    </h3>
                    <p className="text-xs text-white/60">
                      {isExternalPlatform ? "Metadata Sync Guide" : "Embedded Player Guide"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHowTo(false)}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-2 text-white/70 hover:bg-white/[0.06] transition"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-6 pb-20 space-y-6" style={{ maxHeight: "calc(85vh - 100px)" }}>
              {isHost ? (
                // Host Instructions
                <div className="space-y-5">
                  <div className="rounded-2xl border border-violet-400/20 bg-violet-400/5 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="h-5 w-5 text-violet-300" />
                      <h4 className="text-sm font-semibold text-violet-200">You are the Host</h4>
                    </div>
                    <p className="text-sm text-white/70">
                      You have full control over playback. All participants will sync to your actions.
                    </p>
                  </div>

                  {isExternalPlatform ? (
                    // External Platform Host Instructions
                    <>
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">📺 Step 1: Open the Platform</h4>
                        <p className="text-sm text-white/70 mb-2">
                          Click the "Open in {party.platform === "CRUNCHYROLL" ? "Crunchyroll" : party.platform === "NETFLIX" ? "Netflix" : party.platform === "HULU" ? "Hulu" : party.platform === "DISNEY_PLUS" ? "Disney+" : "Funimation"}" button below the instructions.
                          This will open the episode/movie in a new tab.
                        </p>
                        <p className="text-sm text-amber-200/80">
                          ⚠️ Make sure you're logged into your account!
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">🎮 Step 2: Control Your Video</h4>
                        <p className="text-sm text-white/70 mb-2">
                          Play, pause, or seek in YOUR platform tab. Then use the control buttons in this watch party tab to broadcast your action:
                        </p>
                        <ul className="text-sm text-white/70 space-y-1 ml-4">
                          <li>• Click "Play" after you start playing</li>
                          <li>• Click "Pause" after you pause</li>
                          <li>• Use skip buttons or time jump after seeking</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">💬 Step 3: Communicate</h4>
                        <p className="text-sm text-white/70">
                          Participants will see your commands in chat (e.g., "▶️ Host resumed at 5:23").
                          They'll manually sync their video to match.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-4">
                        <p className="text-xs text-amber-200/90">
                          <strong>Why manual sync?</strong> {party.platform === "NETFLIX" ? "Netflix" : party.platform === "CRUNCHYROLL" ? "Crunchyroll" : "These platforms"} don't allow
                          automatic embedding for legal reasons. This ensures everyone uses their own paid subscription.
                        </p>
                      </div>
                    </>
                  ) : (
                    // YouTube/Embedded Host Instructions
                    <>
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">🎮 Control Playback</h4>
                        <p className="text-sm text-white/70">
                          Use the video controls below. All participants will automatically sync to your playback in real-time.
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">⚡ Automatic Sync</h4>
                        <p className="text-sm text-white/70">
                          When you play, pause, or seek, everyone's video will instantly sync. No manual coordination needed!
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                // Participant Instructions
                <div className="space-y-5">
                  <div className="rounded-2xl border border-sky-400/20 bg-sky-400/5 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-sky-300" />
                      <h4 className="text-sm font-semibold text-sky-200">You are a Participant</h4>
                    </div>
                    <p className="text-sm text-white/70">
                      Follow the host's playback. Your video will sync to match theirs.
                    </p>
                  </div>

                  {isExternalPlatform ? (
                    // External Platform Participant Instructions
                    <>
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">📺 Step 1: Open the Platform</h4>
                        <p className="text-sm text-white/70 mb-2">
                          Click the "Open in {party.platform === "CRUNCHYROLL" ? "Crunchyroll" : party.platform === "NETFLIX" ? "Netflix" : party.platform === "HULU" ? "Hulu" : party.platform === "DISNEY_PLUS" ? "Disney+" : "Funimation"}" button below the instructions.
                          This will open the same episode/movie in a new tab.
                        </p>
                        <p className="text-sm text-amber-200/80">
                          ⚠️ You must have your own subscription and be logged in!
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">👀 Step 2: Watch for Commands</h4>
                        <p className="text-sm text-white/70 mb-2">
                          Commands will appear above the player and in chat:
                        </p>
                        <div className="space-y-2 ml-4">
                          <div className="text-sm">
                            <div className="text-emerald-300">"▶️ Host resumed at 5:23"</div>
                            <div className="text-white/60 text-xs">→ Play your video and seek to 5:23</div>
                          </div>
                          <div className="text-sm">
                            <div className="text-emerald-300">"⏸️ Host paused at 10:45"</div>
                            <div className="text-white/60 text-xs">→ Pause your video at 10:45</div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">🔄 Step 3: Manual Sync</h4>
                        <p className="text-sm text-white/70">
                          Keep both tabs open (platform + watch party). When you see a command, manually sync your video to match.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-4">
                        <p className="text-xs text-amber-200/90">
                          <strong>Why manual?</strong> Copyright law requires everyone to use their own account.
                          This ensures you're legally accessing content you've paid for.
                        </p>
                      </div>
                    </>
                  ) : (
                    // YouTube/Embedded Participant Instructions
                    <>
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">⚡ Automatic Sync</h4>
                        <p className="text-sm text-white/70">
                          Your video will automatically play, pause, and seek to match the host. Just sit back and watch!
                        </p>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-emerald-300 mb-3">🎬 Enjoy the Show</h4>
                        <p className="text-sm text-white/70">
                          Everything syncs in real-time. Use the chat to discuss the video with other participants.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* General Tips */}
              <div className="border-t border-white/10 pt-5">
                <h4 className="text-sm font-semibold text-white/80 mb-3">💡 Pro Tips</h4>
                <ul className="text-sm text-white/60 space-y-2">
                  <li>• Use the chat to coordinate with other participants</li>
                  <li>• If you fall behind, let the host know in chat</li>
                  <li>• Keep this tab focused for best performance</li>
                  {isExternalPlatform && <li>• Keep both tabs open (platform + watch party)</li>}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 border-t border-white/10 bg-white/[0.02] p-4 backdrop-blur-xl">
              <button
                onClick={() => setShowHowTo(false)}
                className="w-full rounded-2xl border border-emerald-400/30 bg-emerald-400/10 hover:bg-emerald-400/15 transition px-4 py-3 text-sm font-semibold text-emerald-200"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WatchPartyRoom;
